/**
 * Peak Saunas — UTM Cart Attribute Writer
 *
 * Reads UTM data stored in localStorage by the octane-utm-bridge.js
 * and writes it to Shopify cart attributes so every ORDER captures
 * the attribution data, not just quiz contacts.
 *
 * v1.2 — 2026-08-30: first-touch now EXPIRES (90d TTL, 30d idle reset) — it used to
 *        stick forever, so returning visitors reported a months-old source. And
 *        landing_page is built path-first instead of substring(0,255), which was
 *        chopping the trailing query parameters off long ad URLs.
 *
 * v1.1 — 2026-06-06: robust Google click-id capture — gclid via _gcl_aw
 *        cookie fallback, plus wbraid/gbraid for iOS/privacy traffic — so the
 *        offline conversion upload can match many more orders. Additive only;
 *        first-touch/last-touch UTM logic unchanged.
 */
(function() {
  'use strict';

  var STORAGE_KEY = 'peak_utm';
  // First-touch attribution window. 90d matches GA4's default lookback for
  // non-acquisition key events; 30d idle marks a genuinely new buying journey
  // (Peak's median spend-to-purchase lag is ~12d with a tail past 30d).
  var FT_TTL_DAYS = 90;
  var FT_IDLE_DAYS = 30;
  var GADS_KEY = 'peak_gads';
  var CART_ATTRIBUTES_URL = '/cart/update.js';

  function log(msg, data) {
    if (window.location.hostname === 'localhost' || window.location.search.includes('utm_debug=1')) {
      console.log('[Peak UTM Cart Writer]', msg, data || '');
    }
  }

  function getCookie(name) {
    var prefix = name + '=';
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      if (parts[i].indexOf(prefix) === 0) {
        return decodeURIComponent(parts[i].substring(prefix.length));
      }
    }
    return '';
  }

  // Google Ads click IDs from URL, falling back to Google's first-party cookies
  // (_gcl_aw = "GCL.<ts>.<gclid>", _gcl_gb = "GCL.<ts>.<gbraid>").
  function readGoogleClickIds(params) {
    var ids = {
      gclid:  params.get('gclid')  || '',
      wbraid: params.get('wbraid') || '',
      gbraid: params.get('gbraid') || ''
    };
    if (!ids.gclid) {
      var aw = getCookie('_gcl_aw');
      if (aw) { var a = aw.split('.'); if (a.length >= 3) ids.gclid = a.slice(2).join('.'); }
    }
    if (!ids.gbraid) {
      var gb = getCookie('_gcl_gb');
      if (gb) { var b = gb.split('.'); if (b.length >= 3) ids.gbraid = b.slice(2).join('.'); }
    }
    return ids;
  }

  // Persist click IDs independently of first-touch UTM logic, so gclid/wbraid/
  // gbraid survives even when the first touch was organic.
  function persistGoogleClickIds(ids) {
    if (!ids.gclid && !ids.wbraid && !ids.gbraid) return;
    try {
      var existing = JSON.parse(localStorage.getItem(GADS_KEY) || '{}');
      var merged = {
        gclid:  ids.gclid  || existing.gclid  || '',
        wbraid: ids.wbraid || existing.wbraid || '',
        gbraid: ids.gbraid || existing.gbraid || '',
        ts: new Date().toISOString()
      };
      localStorage.setItem(GADS_KEY, JSON.stringify(merged));
    } catch(e) {}
  }

  function getGoogleClickIds() {
    try { return JSON.parse(localStorage.getItem(GADS_KEY) || '{}'); } catch(e) { return {}; }
  }


  // --- landing_page builder -------------------------------------------------
  // Shopify caps a cart attribute at 255 chars. The old code did a blunt
  // substring(0,255), which chops the END of the URL -- exactly where the
  // campaign parameters (and any future city parameter) live. Order #16549
  // stored a value ending mid-parameter: "...&utm_medium=pmax&utm_".
  // Keep origin+path first (that is what identifies a city landing page), then
  // spend what is left on the most useful parameters. Click IDs are skipped
  // because each already has its own dedicated cart attribute.
  var LP_MAX = 255;
  var LP_SKIP = { fbclid: 1, gclid: 1, gbraid: 1, wbraid: 1, msclkid: 1,
                  _fbp: 1, _fbc: 1, ttclid: 1, twclid: 1, irclickid: 1 };
  var LP_PRIORITY = ['city', 'view', 'utm_term', 'utm_campaign', 'utm_content',
                     'utm_source', 'utm_medium', 'utm_id'];

  function buildLandingPage(href) {
    if (!href) return '';
    var url;
    try { url = new URL(href); } catch (e) { return String(href).substring(0, LP_MAX); }
    var base = url.origin + url.pathname;
    if (base.length >= LP_MAX) return base.substring(0, LP_MAX);

    var params = url.searchParams;
    var seen = {}, ordered = [];
    LP_PRIORITY.forEach(function(k) {
      if (params.has(k) && !LP_SKIP[k]) { ordered.push(k); seen[k] = 1; }
    });
    params.forEach(function(v, k) {
      if (!seen[k] && !LP_SKIP[k]) { ordered.push(k); seen[k] = 1; }
    });

    var out = base, first = true;
    for (var i = 0; i < ordered.length; i++) {
      var k = ordered[i];
      var piece = (first ? '?' : '&') + encodeURIComponent(k) + '=' +
                  encodeURIComponent(params.get(k));
      if (out.length + piece.length > LP_MAX) continue;   // skip, keep trying shorter ones
      out += piece;
      first = false;
    }
    return out;
  }

  // Read UTMs from URL and persist to localStorage (first-touch only)
  function captureUTMs() {
    var params = new URLSearchParams(window.location.search);

    // Always capture/persist Google click IDs (URL or cookie), regardless of UTMs.
    persistGoogleClickIds(readGoogleClickIds(params));

    var utmSource = params.get('utm_source');
    var gclid = params.get('gclid');
    var fbclid = params.get('fbclid');
    var msclkid = params.get('msclkid');

    // Only capture if we have real UTM data
    if (!utmSource && !gclid && !fbclid && !msclkid) return;

    // Determine first_touch_source
    var firstTouch = utmSource || '';
    if (gclid) firstTouch = 'google';
    if (fbclid) firstTouch = 'facebook';
    if (msclkid) firstTouch = 'bing';

    var utmData = {
      src: utmSource || firstTouch,
      med: params.get('utm_medium') || (gclid ? 'cpc' : ''),
      cmp: params.get('utm_campaign') || '',
      cnt: params.get('utm_content') || '',
      trm: params.get('utm_term') || '',
      fts: firstTouch,
      lp: window.location.href,
      gclid: gclid || '',
      fbclid: fbclid || '',
      msclkid: msclkid || '',
      ts: new Date().toISOString()
    };

    // First-touch, with an expiry. The old code kept the very first touch forever,
    // so a returning visitor reported a source from months earlier (order #16551
    // claimed 2026-07-27 while Shopify's own journey said 2026-08-17). A first
    // touch that never expires is not a first touch, it is a fossil.
    var existing = null;
    try { existing = JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch(e) {}

    var now = Date.now();
    var reason = '';
    if (!existing || !existing.fts) {
      reason = 'no existing first-touch';
    } else {
      var startedAt = Date.parse(existing.ts || '');
      var seenAt = Date.parse(existing.seen || existing.ts || '');
      if (!isFinite(startedAt)) {
        reason = 'unparseable timestamp';
      } else if (now - startedAt > FT_TTL_DAYS * 864e5) {
        reason = 'first-touch older than ' + FT_TTL_DAYS + 'd';
      } else if (isFinite(seenAt) && now - seenAt > FT_IDLE_DAYS * 864e5) {
        reason = 'idle more than ' + FT_IDLE_DAYS + 'd, new journey';
      }
    }

    if (reason) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(utmData));
        log('Captured first-touch UTMs (' + reason + '):', utmData);
      } catch(e) {}
    } else {
      // Keep the original first touch, but stamp activity so the idle window is
      // measured from the last real visit rather than from the first one.
      try {
        existing.seen = utmData.ts;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
      } catch(e) {}
      log('First-touch still valid, keeping:', existing.fts);
    }

    // Always update with most recent UTM (last-touch) in a separate key
    try {
      localStorage.setItem('peak_utm_last', JSON.stringify(utmData));
    } catch(e) {}
  }

  // Get stored UTM data
  function getUTMs() {
    // Try window.PeakUTM first (set by other scripts)
    if (window.PeakUTM && window.PeakUTM.get) {
      var d = window.PeakUTM.get();
      if (d && d.fts) return d;
    }
    // Fall back to localStorage
    try {
      var stored = localStorage.getItem(STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch(e) {}
    return null;
  }

  // Write UTM data to Shopify cart attributes
  function writeToCart(utm) {
    if (!utm) utm = {};

    var attributes = {};
    if (utm.src || utm.fts) attributes['utm_source'] = utm.src || utm.fts;
    if (utm.med) attributes['utm_medium'] = utm.med;
    if (utm.cmp) attributes['utm_campaign'] = utm.cmp;
    if (utm.cnt) attributes['utm_content'] = utm.cnt;
    if (utm.trm) attributes['utm_term'] = utm.trm;
    if (utm.fts) attributes['first_touch_source'] = utm.fts;
    if (utm.gclid) attributes['gclid'] = utm.gclid;
    if (utm.fbclid) attributes['fbclid'] = utm.fbclid;
    if (utm.msclkid) attributes['msclkid'] = utm.msclkid;
    if (utm.lp) attributes['landing_page'] = buildLandingPage(utm.lp);
    if (utm.ts) attributes['attribution_ts'] = utm.ts;

    // Overlay the most complete Google click IDs (cookie/last-touch aware) so a
    // gclid/wbraid/gbraid reaches the order even if first-touch was organic.
    var g = getGoogleClickIds();
    if (g.gclid)  attributes['gclid']  = g.gclid;
    if (g.wbraid) attributes['wbraid'] = g.wbraid;
    if (g.gbraid) attributes['gbraid'] = g.gbraid;

    if (Object.keys(attributes).length === 0) return;

    fetch(CART_ATTRIBUTES_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ attributes: attributes })
    })
    .then(function(r) { return r.json(); })
    .then(function(data) {
      log('Cart attributes written:', attributes);
    })
    .catch(function(err) {
      log('Cart write error:', err);
    });
  }

  // Main: capture on page load, write to cart
  function init() {
    // 1. Capture any UTMs / click IDs in current URL or cookies
    captureUTMs();

    // 2. Get stored UTMs (first-touch) and Google click IDs
    var utm = getUTMs();
    var g = getGoogleClickIds();
    var haveClick = g && (g.gclid || g.wbraid || g.gbraid);
    if (!utm && !haveClick) {
      log('No UTM/click data found, skipping cart write');
      return;
    }

    // 3. Write to cart (idempotent — Shopify dedupes identical attribute values)
    writeToCart(utm || {});

    log('Init complete. UTM:', utm);
  }

  // Expose for use by other scripts
  window.PeakUTMCart = {
    get: getUTMs,
    getClickIds: getGoogleClickIds,
    writeToCart: writeToCart,
    capture: captureUTMs
  };

  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run on cart/checkout page events
  document.addEventListener('cart:refresh', function() { writeToCart(getUTMs()); });

})();
